import {useState, useCallback, useMemo} from 'react'
import {Swiper, SwiperSlide} from 'swiper/react'
import {Pagination} from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'
import {luggageIcon} from '@img/index.js'
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import ModalFooter from '@/customComponents/uiComponents/modal/ModalFooter.jsx'
import useScrollOnError from '@/customComponents/uiComponents/modal/useScrollOnError.js'
import Counter from '@/customComponents/uiComponents/global/NumberCounter.jsx'

export default function LuggageSection({
    tripId, luggage, committedLuggage = {}, setLuggage,
    oversizedLuggage, committedOversizedLuggage = {}, setOversizedLuggage,
    luggageTypes = [], onDone, onClose, isLoading, error,
    oversizedMode, setOversizedMode
}) {
    useScrollOnError(error)
    const countsEqual = (a, b) => {
        const allKeys = new Set([...Object.keys(a), ...Object.keys(b)])
        for (const k of allKeys) if ((a[k] || 0) !== (b[k] || 0)) return false
        return true
    }
    const hasChanges = !countsEqual(luggage, committedLuggage) || !countsEqual(oversizedLuggage, committedOversizedLuggage)

    const {standardTypes, oversizedTypes} = useMemo(() => {
        const standard = luggageTypes.filter(l => l.category === 'Standard')
        const oversized = luggageTypes.filter(l => l.category === 'Oversized')
        return {standardTypes: standard, oversizedTypes: oversized}
    }, [luggageTypes])

    const [selectedTypeId, setSelectedTypeId] = useState(null)

    const activeTypes = oversizedMode ? oversizedTypes : standardTypes
    const displayTypes = oversizedMode ? [...oversizedTypes].reverse() : [...standardTypes].reverse()
    const effectiveId = selectedTypeId ?? (activeTypes.length > 0 ? activeTypes[activeTypes.length - 1].id : null)
    const selectedType = activeTypes.find(t => t.id === effectiveId) || activeTypes[activeTypes.length - 1] || null

    const updateLuggage = useCallback((typeId, delta) => {
        setLuggage(prev => ({...prev, [typeId]: Math.max(0, (prev[typeId] || 0) + delta)}))
    }, [setLuggage])

    const updateOversized = useCallback((typeId, delta) => {
        setOversizedLuggage(prev => ({...prev, [typeId]: Math.max(0, (prev[typeId] || 0) + delta)}))
    }, [setOversizedLuggage])

    const getCount = (typeId) => oversizedMode ? (oversizedLuggage[typeId] || 0) : (luggage[typeId] || 0)

    if (luggageTypes.length === 0) {
        return <div className="py-8"><center><Loader/></center></div>
    }

    // Oversized mode
    if (oversizedMode) {
        return (
            <div className="hopn-view-in">
                <div className="flex flex-wrap gap-4 w-full mb-5 max-md:gap-3">
                    {[...oversizedTypes].reverse().map(item => (
                        <div key={item.id} className="border border-gray-300 rounded-3xl p-4 flex flex-col items-center justify-between h-32 text-center w-[166px] max-md:w-[calc(50%-0.375rem)] max-md:p-3">
                            <div className="flex items-start justify-between w-full text-base text-left">
                                <span className="leading-tight">{item.label}</span>
                                <span className="w-10 flex items-center justify-center">
                                    <img src={item.image_url} alt={item.label}/>
                                </span>
                            </div>
                            <div className="flex items-center justify-between overflow-hidden h-10 w-full">
                                <Counter
                                    value={oversizedLuggage[item.id] || 0}
                                    onDecrement={() => updateOversized(item.id, -1)}
                                    onIncrement={() => updateOversized(item.id, 1)}

                                />
                            </div>
                        </div>
                    ))}
                </div>

                {error && <p className="text-red-500 text-sm mt-3">{error}</p>}
                <ModalFooter>
                    <button type="button"
                            onClick={onDone} disabled={!hasChanges || isLoading}
                            className="w-full py-[15px] rounded-full bg-(--text-primary) text-white text-base border-0 cursor-pointer flex items-center justify-center min-h-[55px] disabled:opacity-50">
                        {isLoading ? <Loader color="#fff"/> : 'Done'}
                    </button>
                </ModalFooter>
            </div>
        )
    }

    // Standard luggage mode
    return (
        <div className="hopn-view-in">
            {/* Baggage Display */}
            {selectedType && (
                <>
                    <div className="w-full flex flex-col items-center mb-7.5">
                        <span className="bg-[#b1fbe3] text-(--text-primary) rounded-full inline-flex items-center justify-center text-2xl mb-1.5 w-11 min-w-11 h-11">{selectedType.short_label || selectedType.label}</span>
                        <div className="relative before:content-[''] before:absolute before:border-(--text-primary) before:border-solid before:w-7.5 before:h-7.5 before:border-t-[3px] before:border-l-[3px] before:rounded-tl-[15px] before:top-0 before:left-0 after:content-[''] after:absolute after:border-(--text-primary) after:border-solid after:w-7.5 after:h-7.5 after:border-b-[3px] after:border-r-[3px] after:rounded-br-[15px] after:bottom-0 after:right-0">
                            <div className="relative w-52 h-52 flex justify-center items-end pb-2.5 before:content-[''] before:absolute before:border-(--text-primary) before:border-solid before:w-7.5 before:h-7.5 before:rounded-tr-[15px] before:border-t-[3px] before:border-r-[3px] before:top-0 before:right-0 after:content-[''] after:absolute after:border-(--text-primary) after:border-solid after:w-7.5 after:h-7.5 after:rounded-bl-[15px] after:border-l-[3px] after:border-b-[3px] after:bottom-0 after:left-0 [&_img]:max-h-45 [&_img]:w-auto">
                                {selectedType.dimensions?.height && (
                                    <div className="absolute -left-3 top-[40%] [writing-mode:vertical-rl] rotate-90">
                                        <span>{selectedType.dimensions.height} cm</span>
                                    </div>
                                )}
                                {selectedType.dimensions?.weight && (
                                    <div className="absolute -right-6 top-[40%] bg-(--text-primary) text-white rounded-full min-w-14 h-14 px-3 flex items-center justify-center whitespace-nowrap">
                                        {selectedType.dimensions.weight} kg
                                    </div>
                                )}
                                <img src={selectedType.image_url} alt={selectedType.label}/>
                                {selectedType.dimensions?.width && (
                                    <div className="absolute -bottom-4 right-0 left-0 text-center">
                                        <span>{selectedType.dimensions.width} cm</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Quantity Selector */}
                    <div className="flex items-center justify-center mb-7.5">
                        <div className="bg-(--bg-grey) rounded-full py-0.5 px-1.5 border border-(--bg-dark-grey) flex items-center justify-between min-w-50">
                            <Counter
                                value={luggage[effectiveId] || 0}
                                onDecrement={() => updateLuggage(effectiveId, -1)}
                                onIncrement={() => updateLuggage(effectiveId, 1)}
                            />
                        </div>
                    </div>
                </>
            )}

            {/* Type Selector */}
            <div className="edit-swiper-pagination relative flex flex-col w-full items-stretch mb-5 [&_.swiper]:pb-8 [&_.swiper]:w-full [&_.swiper-wrapper]:items-end [&_.swiper-pagination]:bottom-0! max-md:w-[calc(100%+16px)]">
                <Swiper
                    modules={[Pagination]}
                    spaceBetween={10}
                    pagination={displayTypes.length > 3 ? {clickable: true} : false}
                    breakpoints={{
                        0: {slidesPerView: 'auto', spaceBetween: 10},
                        576: {slidesPerView: 4.5},
                        767: {slidesPerView: 6},
                    }}
                >
                    {displayTypes.map((type, index) => {
                        const isActive = type.id === effectiveId
                        const boxHeightPx = Math.max(45, 95 - index * 10)
                        const boxWidthPx = Math.max(46, 96 - index * 10)
                        return (
                            <SwiperSlide key={type.id} className="max-md:w-auto!">
                                <button
                                    type="button"
                                    className="border-0 outline-0 bg-transparent p-0 cursor-pointer flex flex-col items-center justify-end py-2.5 rounded-[10px] transition-colors duration-200 w-full max-md:w-auto"
                                    onClick={() => setSelectedTypeId(type.id)}
                                >
                                    <span
                                        className={`relative border border-[#ccc] rounded-[10px] mb-1.5 flex items-center justify-center max-md:[width:var(--ico-w)] max-md:[height:var(--ico-h)] md:w-24 md:h-24 ${isActive ? 'bg-(--bg-grey)' : ''}`}
                                        style={{'--ico-w': `${boxWidthPx}px`, '--ico-h': `${boxHeightPx}px`}}
                                    >
                                        {getCount(type.id) > 0 && (
                                            <div className="absolute bg-(--text-primary) text-white left-0 -top-3 w-[25px] h-[25px] flex items-center justify-center rounded-full text-sm font-semibold">{getCount(type.id)}</div>
                                        )}
                                        <img src={type.icon_url || type.image_url || luggageIcon} alt={type.label} className="max-w-[80%] max-h-[80%] w-auto h-auto object-contain"/>
                                    </span>
                                    <span className="text-sm text-center leading-normal text-(--text-primary)">{type.short_label || type.label}</span>
                                </button>
                            </SwiperSlide>
                        )
                    })}
                </Swiper>

            </div>

            {/* Error + Done */}
            {error && <p className="text-red-500 text-sm mt-3">{error}</p>}
            <ModalFooter>
                <button type="button"
                        onClick={onDone} disabled={!hasChanges || isLoading}
                        className="w-full py-3.75 rounded-full bg-(--text-primary) text-white text-base border-0 cursor-pointer flex items-center justify-center min-h-13.75 disabled:opacity-50">
                    {isLoading ? <Loader color="#fff"/> : 'Done'}
                </button>
            </ModalFooter>
        </div>
    )
}
